import { existsSync, readFileSync, writeFileSync, mkdirSync } from 'fs';
import { join, resolve } from 'path';

// =============================================================================
// Types
// =============================================================================

export interface PromptEntry {
  file: string;
  line: number;
  preview: string;
  platformId?: string;
  version?: string;
  status: 'discovered' | 'uploaded';
}

export interface DatasetEntry {
  file: string;
  name: string;
  items: number;
  platformId?: string;
  promptId?: string;
  status: 'discovered' | 'uploaded';
}

export interface EvaluationEntry {
  name: string;
  criteriaCount: number;
  platformId?: string;
  promptId?: string;
  status: 'discovered' | 'uploaded';
}

export interface IntegrationEntry {
  framework: string;
  pkg: string;
  status: string;
}

// =============================================================================
// Markdown Table Helpers
// =============================================================================

function parseTableRows(lines: string[]): string[][] {
  const rows: string[][] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('|') || !trimmed.endsWith('|')) continue;
    // Skip separator rows (e.g. |------|------|)
    if (/^\|[\s-:|]+\|$/.test(trimmed)) continue;
    const cells = trimmed
      .slice(1, -1) // strip outer pipes
      .split('|')
      .map(c => c.trim());
    rows.push(cells);
  }
  return rows;
}

function emptyToUndefined(value: string | undefined): string | undefined {
  return value && value.length > 0 ? value : undefined;
}

function renderTable(headers: string[], rows: string[][]): string {
  const separator = headers.map(h => '-'.repeat(Math.max(h.length, 3)));
  const lines = [
    `| ${headers.join(' | ')} |`,
    `| ${separator.join(' | ')} |`,
    ...rows.map(r => `| ${r.join(' | ')} |`),
  ];
  return lines.join('\n');
}

// =============================================================================
// MutationContext
// =============================================================================

const CONTEXT_DIR = '.mutagent';
const CONTEXT_FILE = 'mutation-context.md';

export class MutationContext {
  private prompts: PromptEntry[] = [];
  private datasets: DatasetEntry[] = [];
  private evaluations: EvaluationEntry[] = [];
  private integrations: IntegrationEntry[] = [];
  private filePath: string;

  private constructor(filePath: string) {
    this.filePath = filePath;
  }

  static load(projectRoot?: string): MutationContext {
    const root = projectRoot ?? process.cwd();
    const dirPath = join(resolve(root), CONTEXT_DIR);
    const filePath = join(dirPath, CONTEXT_FILE);

    const ctx = new MutationContext(filePath);

    if (existsSync(filePath)) {
      const content = readFileSync(filePath, 'utf-8');
      ctx.parse(content);
    }

    return ctx;
  }

  save(): void {
    const dirPath = join(this.filePath, '..');
    if (!existsSync(dirPath)) {
      mkdirSync(dirPath, { recursive: true });
    }
    writeFileSync(this.filePath, this.render(), 'utf-8');
  }

  // ---------------------------------------------------------------------------
  // Prompt operations
  // ---------------------------------------------------------------------------

  addDiscoveredPrompt(file: string, line: number, preview: string): void {
    const existing = this.prompts.find(p => p.file === file && p.line === line);
    if (existing) {
      existing.preview = preview;
      return;
    }
    this.prompts.push({ file, line, preview, status: 'discovered' });
  }

  markPromptUploaded(file: string, platformId: string, version: string): void {
    const entry = this.prompts.find(p => p.file === file);
    if (entry) {
      entry.platformId = platformId;
      entry.version = version;
      entry.status = 'uploaded';
    }
  }

  getDiscoveredPrompts(): PromptEntry[] {
    return this.prompts.filter(p => p.status === 'discovered');
  }

  getUploadedPrompts(): PromptEntry[] {
    return this.prompts.filter(p => p.status === 'uploaded');
  }

  // ---------------------------------------------------------------------------
  // Dataset operations
  // ---------------------------------------------------------------------------

  addDiscoveredDataset(file: string, name: string, items: number): void {
    const existing = this.datasets.find(d => d.file === file && d.name === name);
    if (existing) {
      existing.items = items;
      return;
    }
    this.datasets.push({ file, name, items, status: 'discovered' });
  }

  markDatasetUploaded(file: string, platformId: string, promptId: string): void {
    const entry = this.datasets.find(d => d.file === file);
    if (entry) {
      entry.platformId = platformId;
      entry.promptId = promptId;
      entry.status = 'uploaded';
    }
  }

  // ---------------------------------------------------------------------------
  // Evaluation operations
  // ---------------------------------------------------------------------------

  addEvaluation(name: string, criteriaCount: number, platformId: string, promptId: string): void {
    const existing = this.evaluations.find(e => e.name === name);
    if (existing) {
      existing.criteriaCount = criteriaCount;
      existing.platformId = platformId;
      existing.promptId = promptId;
      existing.status = 'uploaded';
      return;
    }
    this.evaluations.push({ name, criteriaCount, platformId, promptId, status: 'uploaded' });
  }

  // ---------------------------------------------------------------------------
  // Integration operations
  // ---------------------------------------------------------------------------

  setIntegrationStatus(framework: string, pkg: string, status: string): void {
    const existing = this.integrations.find(i => i.framework === framework);
    if (existing) {
      existing.pkg = pkg;
      existing.status = status;
      return;
    }
    this.integrations.push({ framework, pkg, status });
  }

  // ---------------------------------------------------------------------------
  // Summary
  // ---------------------------------------------------------------------------

  getSummary(): { prompts: number; datasets: number; evaluations: number; integrated: string[] } {
    return {
      prompts: this.prompts.length,
      datasets: this.datasets.length,
      evaluations: this.evaluations.length,
      integrated: this.integrations.map(i => i.framework),
    };
  }

  // ---------------------------------------------------------------------------
  // Serialization
  // ---------------------------------------------------------------------------

  private render(): string {
    const now = new Date().toISOString();
    const sections: string[] = [
      '# MutagenT Mutation Context',
      `<!-- MutagenT:AUTO-GENERATED - Do not edit manually -->`,
      `<!-- Last updated: ${now} -->`,
      '',
      '## Discovered Resources',
      '',
      '### Prompts',
      renderTable(
        ['File', 'Line', 'Content Preview', 'Platform ID', 'Version', 'Status'],
        this.prompts.map(p => [
          p.file,
          String(p.line),
          p.preview,
          p.platformId ?? '',
          p.version ?? '',
          p.status,
        ]),
      ),
      '',
      '### Datasets',
      renderTable(
        ['File', 'Name', 'Items', 'Platform ID', 'Prompt ID', 'Status'],
        this.datasets.map(d => [
          d.file,
          d.name,
          String(d.items),
          d.platformId ?? '',
          d.promptId ?? '',
          d.status,
        ]),
      ),
      '',
      '### Evaluations',
      renderTable(
        ['Name', 'Criteria Count', 'Platform ID', 'Prompt ID', 'Status'],
        this.evaluations.map(e => [
          e.name,
          String(e.criteriaCount),
          e.platformId ?? '',
          e.promptId ?? '',
          e.status,
        ]),
      ),
      '',
      '## Integration Status',
      renderTable(
        ['Framework', 'Package', 'Status'],
        this.integrations.map(i => [
          i.framework,
          i.pkg,
          i.status,
        ]),
      ),
      '',
    ];

    return sections.join('\n');
  }

  private parse(content: string): void {
    const lines = content.split('\n');

    let currentSection: 'prompts' | 'datasets' | 'evaluations' | 'integrations' | null = null;
    let sectionLines: string[] = [];

    const flushSection = (): void => {
      if (!currentSection || sectionLines.length === 0) return;

      const rows = parseTableRows(sectionLines);
      // Skip header row (first row after parsing)
      const dataRows = rows.slice(1);

      switch (currentSection) {
        case 'prompts':
          for (const row of dataRows) {
            const [file, lineStr, preview, pid, ver, st] = row;
            if (file !== undefined && lineStr !== undefined && preview !== undefined) {
              this.prompts.push({
                file,
                line: parseInt(lineStr, 10) || 0,
                preview,
                platformId: emptyToUndefined(pid),
                version: emptyToUndefined(ver),
                status: (emptyToUndefined(st) as PromptEntry['status'] | undefined) ?? 'discovered',
              });
            }
          }
          break;
        case 'datasets':
          for (const row of dataRows) {
            const [file, name, itemsStr, pid, prid, st] = row;
            if (file !== undefined && name !== undefined && itemsStr !== undefined) {
              this.datasets.push({
                file,
                name,
                items: parseInt(itemsStr, 10) || 0,
                platformId: emptyToUndefined(pid),
                promptId: emptyToUndefined(prid),
                status: (emptyToUndefined(st) as DatasetEntry['status'] | undefined) ?? 'discovered',
              });
            }
          }
          break;
        case 'evaluations':
          for (const row of dataRows) {
            const [name, ccStr, pid, prid, st] = row;
            if (name !== undefined && ccStr !== undefined) {
              this.evaluations.push({
                name,
                criteriaCount: parseInt(ccStr, 10) || 0,
                platformId: emptyToUndefined(pid),
                promptId: emptyToUndefined(prid),
                status: (emptyToUndefined(st) as EvaluationEntry['status'] | undefined) ?? 'discovered',
              });
            }
          }
          break;
        case 'integrations':
          for (const row of dataRows) {
            const [framework, pkg, status] = row;
            if (framework !== undefined && pkg !== undefined && status !== undefined) {
              this.integrations.push({ framework, pkg, status });
            }
          }
          break;
      }

      sectionLines = [];
    };

    for (const line of lines) {
      const trimmed = line.trim();

      if (trimmed === '### Prompts') {
        flushSection();
        currentSection = 'prompts';
        continue;
      }
      if (trimmed === '### Datasets') {
        flushSection();
        currentSection = 'datasets';
        continue;
      }
      if (trimmed === '### Evaluations') {
        flushSection();
        currentSection = 'evaluations';
        continue;
      }
      if (trimmed === '## Integration Status') {
        flushSection();
        currentSection = 'integrations';
        continue;
      }
      // New section heading resets current context
      if (trimmed.startsWith('## ') && currentSection !== null && trimmed !== '## Integration Status') {
        flushSection();
        currentSection = null;
        continue;
      }

      if (currentSection && trimmed.startsWith('|')) {
        sectionLines.push(trimmed);
      }
    }

    flushSection();
  }
}
